/**
 * File-group membership, worked out in the browser.
 *
 * A file may sit in several groups (FileGroup ↔ FileGroupFile ↔ File), so
 * "Ungrouped" means "in none of them" and a move is an add then a remove.
 * The client's file list does not say which groups a file is in, so the
 * Client Documents screen reads every group's detail and derives the rest
 * here. Pure functions: no React, no fetching.
 */

import { setModuleParam, type ModuleCode } from '../modules/modules';

const UNGROUPED = 'ungrouped';

/** What the Client Documents rail has chosen. */
export type Selection =
	{ kind: 'all' } | { kind: 'ungrouped' } | { kind: 'group'; id: string };

export type MemberMap = Map<string, Set<string>>;

export function membersByGroup(
	details: ReadonlyArray<{
		pkFileGroupId: string;
		files: ReadonlyArray<{ pkFileId: string }>;
	}>,
): MemberMap {
	return new Map(
		details.map((detail) => [
			detail.pkFileGroupId,
			new Set(detail.files.map((file) => file.pkFileId)),
		]),
	);
}

export function ungroupedIds(
	allFileIds: Iterable<string>,
	members: MemberMap,
): Set<string> {
	const grouped = new Set<string>();
	for (const ids of members.values()) for (const id of ids) grouped.add(id);
	return new Set([...allFileIds].filter((id) => !grouped.has(id)));
}

/** The groups that adding (or moving) `fileIds` would actually change: at
 *  least one of them is not in the group yet. A group whose membership has
 *  not loaded is left out rather than guessed at. */
export function addableGroups<G extends { pkFileGroupId: string }>(
	groups: ReadonlyArray<G>,
	members: MemberMap,
	fileIds: ReadonlyArray<string>,
): G[] {
	return groups.filter((group) => {
		const ids = members.get(group.pkFileGroupId);
		return ids !== undefined && fileIds.some((id) => !ids.has(id));
	});
}

/** The whole membership to PUT after adding `ids`. */
export function withAdded(
	current: Iterable<string>,
	ids: Iterable<string>,
): string[] {
	return [...new Set([...current, ...ids])];
}

/** The whole membership to PUT after removing `ids`. */
export function withRemoved(
	current: Iterable<string>,
	ids: Iterable<string>,
): string[] {
	const drop = new Set(ids);
	return [...new Set(current)].filter((id) => !drop.has(id));
}

/** The ticked ids still on screen — what a bulk action may touch. */
export function keepVisible(
	selected: ReadonlySet<string>,
	visibleIds: Iterable<string>,
): string[] {
	return [...visibleIds].filter((id) => selected.has(id));
}

/** Another group of this client already has `name` (trimmed, exact match —
 *  the database's unique index is case-sensitive). `exceptId` is the group
 *  being renamed, which may keep its own name. */
export function duplicateGroupName(
	name: string,
	groups: ReadonlyArray<{ pkFileGroupId: string; name: string }>,
	exceptId?: string,
): boolean {
	const wanted = name.trim();
	if (wanted === '') return false;
	return groups.some(
		(group) => group.pkFileGroupId !== exceptId && group.name === wanted,
	);
}

/** `?group=<id>` | `?group=ungrouped` | nothing. The old `?folder=` (`others`
 *  or a group id) is read too, and flagged so the caller rewrites the URL. */
export function selectionFromParams(
	group: string | null,
	folder: string | null,
): { selection: Selection; legacy: boolean } {
	if (group === UNGROUPED)
		return { selection: { kind: 'ungrouped' }, legacy: false };
	if (group)
		return { selection: { kind: 'group', id: group }, legacy: false };
	if (folder === 'others')
		return { selection: { kind: 'all' }, legacy: true };
	if (folder)
		return { selection: { kind: 'group', id: folder }, legacy: true };
	return { selection: { kind: 'all' }, legacy: false };
}

export function selectionParams(selection: Selection): Record<string, string> {
	if (selection.kind === 'all') return {};
	return { group: selection.kind === 'ungrouped' ? UNGROUPED : selection.id };
}

/** The whole search string for a selection: its own params plus `module`,
 *  which `setParams` would otherwise drop (it replaces every param). */
export function selectionSearch(
	selection: Selection,
	module: ModuleCode,
): URLSearchParams {
	return setModuleParam(
		new URLSearchParams(selectionParams(selection)),
		module,
	);
}

export function selectionKey(selection: Selection): string {
	return selection.kind === 'group'
		? `group:${selection.id}`
		: selection.kind;
}

/**
 * Runs async tasks strictly one after another. Membership is saved as a
 * whole set, so two saves to one group in flight at once would each drop the
 * other's change — uploading several files into a group goes through this.
 */
export function createSerialQueue() {
	let tail: Promise<unknown> = Promise.resolve();
	return {
		run<T>(task: () => Promise<T>): Promise<T> {
			const next = tail.then(() => task());
			tail = next.catch(() => undefined);
			return next;
		},
	};
}
